/**
 * Dynamic Detail Page
 * 
 * Displays full details for a single D/s dynamic including:
 * - Name, category, intensity badges
 * - Key stats (intensity, commitment, negotiation complexity)
 * - Description and page sections
 * - Related archetypes, kinks
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    ArrowLeft, Crown, Heart, Shield, Users, Zap,
    ChevronRight, ChevronDown, Sparkles, BookOpen,
    Scale, AlertTriangle, Clock
} from 'lucide-react';
import api from '../services/api';
import Spinner from '../components/Spinner';

// Category colors
const getCategoryColor = (category) => {
    const colors = {
        'power exchange': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
        'service': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
        'protocol': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        'lifestyle': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        'play': 'text-pink-400 bg-pink-500/10 border-pink-500/20',
        'psychological': 'text-rose-400 bg-rose-500/10 border-rose-500/20'
    };
    return colors[category?.toLowerCase()] || 'text-gray-400 bg-gray-500/10 border-gray-500/20';
};

// Section configuration
const SECTION_CONFIG = {
    overview: { icon: BookOpen, title: 'Overview', color: 'text-blue-400' },
    power_flow: { icon: Crown, title: 'Power Structure', color: 'text-purple-400' },
    negotiation: { icon: Scale, title: 'Negotiation', color: 'text-amber-400' },
    daily_protocols: { icon: Clock, title: 'Daily Rituals', color: 'text-emerald-400' },
    maintenance: { icon: Heart, title: 'Maintenance', color: 'text-pink-400' },
    exit_strategy: { icon: ChevronRight, title: 'Ending Gracefully', color: 'text-rose-400' },
    community: { icon: Users, title: 'Community & Spaces', color: 'text-blue-400' },
    red_flags: { icon: AlertTriangle, title: 'Red Flags', color: 'text-red-500' },
    aftercare: { icon: Heart, title: 'Aftercare', color: 'text-pink-400' },
    progression: { icon: Sparkles, title: "What's Next", color: 'text-indigo-400' }
};

// Page section component with accordion
const PageSection = ({ sectionKey, content, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const config = SECTION_CONFIG[sectionKey] || { icon: BookOpen, title: sectionKey, color: 'text-gray-400' };
    const Icon = config.icon;

    if (!content) return null;

    return (
        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300 mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors focus:outline-none"
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <h3 className="font-fantasy font-semibold text-lg text-cream/90 tracking-wide">{config.title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-3 border-t border-wine-700/30">
                    <div className="prose prose-invert max-w-none text-cream/70 leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Related entity link component
const EntityLink = ({ to, name, category }) => (
    <Link
        to={to}
        className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
    >
        <div>
            <span className="text-white group-hover:text-wine-300 transition-colors">{name}</span>
            {category && <span className="ml-2 text-sm text-dark-500">{category}</span>}
        </div>
        <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
    </Link>
);

const DynamicDetailPage = () => {
    const { id } = useParams();
    const [dynamic, setDynamic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDynamic();
    }, [id]);

    const fetchDynamic = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getDynamic(id);
            setDynamic(data);
        } catch (err) {
            console.error('Error fetching dynamic:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading dynamic...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !dynamic) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">👑</div>
                    <h2 className="text-xl font-bold text-white mb-2">Dynamic Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This dynamic does not exist.'}</p>
                    <Link to="/browse?tab=dynamics" className="btn-primary">
                        Back to Dynamics
                    </Link>
                </div>
            </div>
        );
    }

    // Get page sections by key
    const getSectionContent = (key) => {
        const section = dynamic.pageSections?.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    const categoryClass = getCategoryColor(dynamic.category);

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/browse?tab=dynamics"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Dynamics</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {dynamic.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/dynamic/${dynamic.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {dynamic.parentVariant.name}
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <header className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-700/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-purple-700/20 flex items-center justify-center">
                                <Crown size={28} className="text-purple-400" />
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-3 tracking-wide">
                                    {dynamic.name}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    {dynamic.category && (
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryClass}`}>
                                            {dynamic.category}
                                        </span>
                                    )}
                                    {dynamic.intensity_level && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-rose-500/10 text-rose-400">
                                            <Zap size={12} className="inline mr-1" />
                                            Intensity {dynamic.intensity_level}/10
                                        </span>
                                    )}
                                    {dynamic.requires_negotiation && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-amber-500/10 text-amber-400">
                                            <Scale size={12} className="inline mr-1" />
                                            Negotiation Required
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        {dynamic.description && (
                            <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed mt-4">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{dynamic.description}</ReactMarkdown>
                            </div>
                        )}

                        {/* Ornate Divider */}
                        <div className="ornate-divider w-32 my-6" />

                        {/* Key Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Zap size={18} className="mx-auto mb-1 text-rose-400" />
                                <div className="text-lg font-bold text-cream">{dynamic.intensity_level || 5}</div>
                                <div className="text-xs text-dark-400">Intensity</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Clock size={18} className="mx-auto mb-1 text-amber-400" />
                                <div className="text-lg font-bold text-cream">{dynamic.commitment_level || 5}</div>
                                <div className="text-xs text-dark-400">Commitment</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Scale size={18} className="mx-auto mb-1 text-purple-400" />
                                <div className="text-lg font-bold text-cream">{dynamic.negotiation_complexity || 5}</div>
                                <div className="text-xs text-dark-400">Complexity</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Shield size={18} className="mx-auto mb-1 text-emerald-400" />
                                <div className="text-lg font-bold text-cream">{dynamic.safety_requirements || 5}</div>
                                <div className="text-xs text-dark-400">Safety Level</div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Sections */}
                <div className="space-y-4">
                    {Object.keys(SECTION_CONFIG).map(key => (
                        <PageSection
                            key={key}
                            sectionKey={key}
                            content={getSectionContent(key)}
                            defaultOpen={key === 'overview' || key === 'power_flow'}
                        />
                    ))}
                </div>

                {/* Sub-Variants */}
                {dynamic.childVariants && dynamic.childVariants.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Crown className="w-5 h-5 text-purple-400" />
                            Variants of {dynamic.name}
                        </h3>
                        <div className="grid gap-2">
                            {dynamic.childVariants.map(variant => (
                                <EntityLink
                                    key={variant.id}
                                    to={`/dynamic/${variant.id}`}
                                    name={variant.name}
                                    category={variant.category}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Related Entities */}
                {dynamic.relatedArchetypes?.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-400" />
                            Compatible Archetypes
                        </h3>
                        <div className="grid gap-2">
                            {dynamic.relatedArchetypes.map(arch => (
                                <EntityLink
                                    key={arch.id}
                                    to={`/archetype/${arch.id}`}
                                    name={arch.name}
                                    category={arch.category}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {dynamic.relatedKinks?.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-wine-400" />
                            Associated Kinks
                        </h3>
                        <div className="grid gap-2">
                            {dynamic.relatedKinks.map(kink => (
                                <EntityLink
                                    key={kink.id}
                                    to={`/kink/${kink.id}`}
                                    name={kink.name}
                                    category={kink.category}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default DynamicDetailPage;
